import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { requireCapability } from '@/lib/auth'
import { RoutePending } from '@/components/route-pending'
import { RouteError } from '@/components/route-error'
import { reportRangeSearchSchema } from '@/features/reports/components/report-range-search'
import { RegistersReportPage } from '@/features/reports/pages/registers'

import type { RegistersReportFilters, RegistersReportPaging, ReportRangeSelection } from '@/types'

const PAGE_SIZE = 50

/* The Registers report: every register session with its sales and cash. It
   replaces the old X-Report page — that page's range totals and payment
   breakdown are this report's header. Register / cashier / status live in the
   URL beside the range so a filtered view can be shared or bookmarked. */
const searchSchema = reportRangeSearchSchema.extend({
  registerId: z.string().optional(),
  userId: z.string().optional(),
  status: z.enum(['OPEN', 'CLOSED']).optional(),
  overOnly: z.boolean().optional(),
  page: z.number().int().min(1).optional(),
  limit: z.number().int().min(1).max(200).optional(),
  /** The session open in the right panel (the app's side-panel rule: selection lives in the URL). */
  session: z.string().optional(),
})

export const Route = createFileRoute('/_dashboard/reports/registers')({
  beforeLoad: requireCapability('VIEW_REPORTS'),
  validateSearch: searchSchema,
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: RegistersRoute,
})

function RegistersRoute() {
  const { registerId, userId, status, overOnly, page, limit, session, ...range } = Route.useSearch()
  const navigate = Route.useNavigate()
  // A register report is read for a day first: default the range to today.
  const selection: ReportRangeSelection = range.mode === 'preset' && !range.preset ? { ...range, preset: 'today' } : range
  const filters: RegistersReportFilters = { registerId, userId, status, overOnly }
  const paging: RegistersReportPaging = { page: page ?? 1, limit: limit ?? PAGE_SIZE }
  return (
    <RegistersReportPage
      selection={selection}
      filters={filters}
      paging={paging}
      // A new range or filter is a new list: back to page 1.
      onSelectionChange={(next) => navigate({ search: (prev) => ({ ...prev, ...next, page: undefined }), replace: true })}
      onFiltersChange={(next) => navigate({ search: (prev) => ({ ...prev, ...next, overOnly: next.overOnly || undefined, page: undefined, session: undefined }), replace: true })}
      onPagingChange={(next) => navigate({ search: (prev) => ({ ...prev, page: next.page > 1 ? next.page : undefined, limit: next.limit !== PAGE_SIZE ? next.limit : undefined }), replace: true })}
      selectedId={session}
      onSelect={(id) => navigate({ search: (prev) => ({ ...prev, session: id ?? undefined }), replace: true })}
    />
  )
}
